import type { ServiceEngagementStatus, ServiceEngagementType } from '../../services/serviceEngagementService';
import type { DirectoryActivityType } from '../../services/professionalsService';

/**
 * Shared facts for the professional-services page. The icons are the ones
 * roleCatalog and the professionals directory already use for the matching
 * professional, so a service looks the same everywhere.
 */
export const ENGAGEMENT_ICONS: Record<ServiceEngagementType, string> = {
  APPRAISAL: 'ri-scales-3-line',
  CONSULTING: 'ri-customer-service-2-line',
  ANALYSIS: 'ri-line-chart-line',
  PROPERTY_MANAGEMENT: 'ri-building-2-line',
  FACILITIES_MANAGEMENT: 'ri-tools-line',
};

/** The provider's path through a request. CANCELLED leaves it from any step. */
export const STATUS_FLOW: ServiceEngagementStatus[] = [
  'REQUESTED',
  'QUOTED',
  'ACCEPTED',
  'IN_PROGRESS',
  'DELIVERED',
  'COMPLETED',
];

/** The step after `status`, or null once the request is completed or cancelled. */
export function nextStatus(status: ServiceEngagementStatus): ServiceEngagementStatus | null {
  const index = STATUS_FLOW.indexOf(status);
  return index >= 0 && index < STATUS_FLOW.length - 1 ? STATUS_FLOW[index + 1] : null;
}

/**
 * Only complete http(s) links are rendered or sent. This blocks javascript: and
 * data: URLs (stored XSS through a provider-supplied deliverableUrl) and matches
 * the backend's own check (z.string().url() refined to http(s)).
 */
export function safeHttpUrl(url?: string | null): string | null {
  if (!url || !/^https?:\/\//i.test(url)) return null;
  try {
    return new URL(url).host ? url : null;
  } catch {
    return null;
  }
}

/** An AccountActivity id, the shape the backend accepts (z.string().uuid()). */
export const PROVIDER_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The public professionals directory is flag-gated. Read on each call, so tests can stub it. */
export function directoryEnabled(): boolean {
  return import.meta.env.VITE_PROFESSIONALS_DIRECTORY_ENABLED === 'true';
}

/**
 * The directory section that lists the providers of each service. Analysts are
 * not in the directory, and consultants are hidden from it on the owner's
 * instruction (DIRECTORY_TYPES in services/professionalsService.ts), so those
 * two services take the provider's activity id instead.
 */
const DIRECTORY_TYPE_FOR: Partial<Record<ServiceEngagementType, DirectoryActivityType>> = {
  APPRAISAL: 'APPRAISER',
  PROPERTY_MANAGEMENT: 'PROPERTY_MANAGEMENT',
  FACILITIES_MANAGEMENT: 'FACILITIES_MANAGEMENT',
};

export function directoryTypeFor(type: ServiceEngagementType): DirectoryActivityType | null {
  return DIRECTORY_TYPE_FOR[type] ?? null;
}
